/**
 * Content blocks: text, hero, feature, comparison
 * Simple content display blocks with no interactivity.
 */

import { registerBlockRenderer, getBlockRendererContext } from './registry';
import { escapeHtml, createBlockWrapper } from './utils';
import { iconCheck, iconX } from '../icons';
import type { TextData, HeroData, FeatureData, ComparisonData } from './types';

// =============================================================================
// TEXT BLOCK
// =============================================================================

registerBlockRenderer('text', (data: unknown, container: HTMLElement) => {
  const textData = data as TextData;
  const wrapper = createBlockWrapper('text');
  wrapper.textContent = textData.content ?? '';
  container.appendChild(wrapper);
});

// =============================================================================
// HERO BLOCK
// =============================================================================

registerBlockRenderer('hero', (data: unknown, container: HTMLElement) => {
  const heroData = data as HeroData & { headline?: string; subheadline?: string };
  const wrapper = createBlockWrapper('hero');

  let html = '';

  if (heroData.highlight) {
    html += `<div class="pms-hero-highlight">${escapeHtml(heroData.highlight)}</div>`;
  }

  // Support both title/subtitle and headline/subheadline field names
  const title = heroData.title || heroData.headline;
  const subtitle = heroData.subtitle || heroData.subheadline;

  if (title) {
    html += `<h2 class="pms-hero-title">${escapeHtml(title)}</h2>`;
  }

  if (subtitle) {
    html += `<p class="pms-hero-subtitle">${escapeHtml(subtitle)}</p>`;
  }

  wrapper.innerHTML = html;

  // Vertical signature icon — prepend after innerHTML to preserve content
  const vertical = getBlockRendererContext().vertical;

  if (vertical === 'saas' || vertical === 'tech') {
    const dots = document.createElement('div');
    dots.className = 'pms-hero-terminal-dots';
    wrapper.prepend(dots);
  }

  if (vertical === 'restaurant') {
    const stamp = document.createElement('div');
    stamp.className = 'pms-hero-ticket-stamp';
    stamp.textContent = 'FEATURED';
    wrapper.prepend(stamp);
  }

  if (vertical === 'ecommerce') {
    const tag = document.createElement('div');
    tag.className = 'pms-hero-tag';
    wrapper.prepend(tag);
  }

  if (vertical === 'healthcare') {
    const cross = document.createElement('div');
    cross.className = 'pms-hero-cross';
    wrapper.prepend(cross);
  }

  if (vertical === 'finance' || vertical === 'fintech') {
    const badge = document.createElement('div');
    badge.className = 'pms-hero-secure-badge';
    badge.textContent = 'VERIFIED';
    wrapper.prepend(badge);
  }

  if (vertical === 'real_estate') {
    const compass = document.createElement('div');
    compass.className = 'pms-hero-compass';
    wrapper.prepend(compass);
  }

  if (vertical === 'automotive') {
    const gauge = document.createElement('div');
    gauge.className = 'pms-hero-gauge-tick';
    wrapper.prepend(gauge);
  }

  if (vertical === 'portfolio') {
    const cursor = document.createElement('div');
    cursor.className = 'pms-hero-cursor';
    wrapper.prepend(cursor);
  }

  if (vertical === 'professional_services') {
    const seal = document.createElement('div');
    seal.className = 'pms-hero-seal';
    wrapper.prepend(seal);
  }

  if (vertical === 'dental') {
    // Tooth icon (top-right signature)
    const tooth = document.createElement('div');
    tooth.className = 'pms-hero-tooth';
    wrapper.prepend(tooth);
    // Status indicator (available/booked — like a pulse dot)
    const statusDot = document.createElement('div');
    statusDot.className = 'pms-hero-dental-pulse';
    wrapper.prepend(statusDot);
  }

  if (vertical === 'agency') {
    const broadcast = document.createElement('div');
    broadcast.className = 'pms-hero-broadcast';
    wrapper.prepend(broadcast);
  }

  if (vertical === 'education') {
    const seal = document.createElement('div');
    seal.className = 'pms-hero-academic-seal';
    wrapper.prepend(seal);
  }

  if (vertical === 'nonprofit') {
    const ripple = document.createElement('div');
    ripple.className = 'pms-hero-ripple';
    wrapper.prepend(ripple);
  }

  if (vertical === 'legal') {
    const column = document.createElement('div');
    column.className = 'pms-hero-column';
    wrapper.prepend(column);
    const columnRight = document.createElement('div');
    columnRight.className = 'pms-hero-column-right';
    wrapper.prepend(columnRight);
  }

  container.appendChild(wrapper);
});

// =============================================================================
// FEATURE BLOCK
// =============================================================================

registerBlockRenderer('feature', (data: unknown, container: HTMLElement) => {
  const featureData = data as FeatureData & { title?: string };
  const wrapper = createBlockWrapper('feature');

  let html = '';

  if (featureData.highlight) {
    html += `<div class="pms-feature-highlight">${escapeHtml(featureData.highlight)}</div>`;
  }

  // Support both headline and title field names (backend uses title)
  const headline = featureData.headline || featureData.title;
  if (headline) {
    html += `<div class="pms-feature-headline">${escapeHtml(headline)}</div>`;
  }

  if (featureData.description) {
    html += `<div class="pms-feature-description">${escapeHtml(featureData.description)}</div>`;
  }

  if (featureData.metrics && featureData.metrics.length > 0) {
    html += '<div class="pms-feature-metrics">';
    for (const metric of featureData.metrics) {
      html += `<div class="pms-feature-metric">
        <span class="pms-feature-metric-value">${escapeHtml(metric.value)}</span>
        <span class="pms-feature-metric-label">${escapeHtml(metric.label)}</span>
      </div>`;
    }
    html += '</div>';
  }

  wrapper.innerHTML = html;

  // Vertical signature elements — prepend after innerHTML to preserve content
  const vertical = getBlockRendererContext().vertical;

  if (vertical === 'saas' || vertical === 'tech') {
    const dot = document.createElement('div');
    dot.className = 'pms-feature-status-dot';
    wrapper.prepend(dot);
  }

  if (vertical === 'restaurant') {
    const stamp = document.createElement('div');
    stamp.className = 'pms-feature-order-tag';
    stamp.textContent = 'SPECIAL';
    wrapper.prepend(stamp);
  }

  if (vertical === 'ecommerce') {
    const tag = document.createElement('div');
    tag.className = 'pms-feature-price-tag';
    wrapper.prepend(tag);
  }

  if (vertical === 'healthcare') {
    const pulse = document.createElement('div');
    pulse.className = 'pms-feature-pulse';
    wrapper.prepend(pulse);
  }

  if (vertical === 'finance' || vertical === 'fintech') {
    const bar = document.createElement('div');
    bar.className = 'pms-feature-chart-bar';
    wrapper.prepend(bar);
  }

  if (vertical === 'real_estate') {
    const pin = document.createElement('div');
    pin.className = 'pms-feature-pin';
    wrapper.prepend(pin);
  }

  if (vertical === 'automotive') {
    const gauge = document.createElement('div');
    gauge.className = 'pms-feature-tach';
    wrapper.prepend(gauge);
  }

  if (vertical === 'portfolio') {
    const corner = document.createElement('div');
    corner.className = 'pms-feature-crop-marks';
    wrapper.prepend(corner);
  }

  if (vertical === 'professional_services') {
    const seal = document.createElement('div');
    seal.className = 'pms-feature-footnote';
    wrapper.prepend(seal);
  }

  if (vertical === 'agency') {
    const megaphone = document.createElement('div');
    megaphone.className = 'pms-feature-broadcast';
    wrapper.prepend(megaphone);
  }

  if (vertical === 'education') {
    const bookmark = document.createElement('div');
    bookmark.className = 'pms-feature-bookmark';
    wrapper.prepend(bookmark);
  }

  if (vertical === 'nonprofit') {
    const leaf = document.createElement('div');
    leaf.className = 'pms-feature-leaf';
    wrapper.prepend(leaf);
  }

  if (vertical === 'legal') {
    const pillar = document.createElement('div');
    pillar.className = 'pms-feature-pillar';
    wrapper.prepend(pillar);
  }

  container.appendChild(wrapper);
});

// =============================================================================
// COMPARISON BLOCK
// =============================================================================

registerBlockRenderer('comparison', (data: unknown, container: HTMLElement) => {
  const compData = data as ComparisonData;
  const wrapper = createBlockWrapper('comparison');

  const grid = document.createElement('div');
  grid.className = 'pms-comparison-grid';

  // Small X icon for before items
  const iconSmallX = () => `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M18 6L6 18M6 6l12 12"/></svg>`;
  // Small check icon for after items
  const iconSmallCheck = () => `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M20 6L9 17l-5-5"/></svg>`;

  if (compData.before) {
    const beforeEl = document.createElement('div');
    beforeEl.className = 'pms-comparison-side pms-comparison-before';

    let html = `<div class="pms-comparison-header"><span class="pms-comparison-icon">${iconX()}</span><span class="pms-comparison-title">${escapeHtml(compData.before.title ?? 'Before')}</span></div>`;
    if (compData.before.items && compData.before.items.length > 0) {
      html += '<ul class="pms-comparison-list">';
      for (const item of compData.before.items) {
        html += `<li><span class="pms-comparison-marker">${iconSmallX()}</span><span>${escapeHtml(item)}</span></li>`;
      }
      html += '</ul>';
    }

    beforeEl.innerHTML = html;
    grid.appendChild(beforeEl);
  }

  if (compData.after) {
    const afterEl = document.createElement('div');
    afterEl.className = 'pms-comparison-side pms-comparison-after';

    let html = `<div class="pms-comparison-header"><span class="pms-comparison-icon">${iconCheck()}</span><span class="pms-comparison-title">${escapeHtml(compData.after.title ?? 'After')}</span></div>`;
    if (compData.after.items && compData.after.items.length > 0) {
      html += '<ul class="pms-comparison-list">';
      for (const item of compData.after.items) {
        html += `<li><span class="pms-comparison-marker">${iconSmallCheck()}</span><span>${escapeHtml(item)}</span></li>`;
      }
      html += '</ul>';
    }

    afterEl.innerHTML = html;
    grid.appendChild(afterEl);
  }

  wrapper.appendChild(grid);
  container.appendChild(wrapper);
});
